// Ask Lume (contracts/lume.md §2): the in-app chat powered by Claude. Admins tune it (config lume.chat: on/off,
// questions per day, model, reply length, monthly budget), watch usage and cost per day against the budget, and
// review the latest 100 turns for quality. Other roles see what the feature sends and keeps, nothing else.

import { useState } from "react";
import { Link } from "react-router";
import { CHAT_STATUSES, type ChatStatus, type ChatTurn, useChatStats } from "../api/lume";
import { useConfig, useSetConfig } from "../api/queries";
import { useAuth, useCan } from "../auth/AuthContext";
import { ColumnChart } from "../components/charts/charts";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Select, Stat, TextInput } from "../components/ui";
import { addDays, formatDateTime, formatShortDate, todayUtc } from "../lib/dates";
import { fmtInt, fmtPct, shortId } from "../lib/format";
import { chatProblem, type LumeChatConfig, readLumeConfig } from "../lib/lume";
import al from "./asklume.module.css";
import { SwitchRow } from "./messaging/components";
import m from "./messaging/messaging.module.css";
import styles from "./pages.module.css";

const usd = (v: number, digits = 2) => `$${v.toFixed(digits)}`;
const STATUS_TONE: Record<ChatStatus, string> = { answered: "success", refused: "warning", failed: "danger" };

export function AskLumePage() {
  const canChat = useCan("lume.chat");
  const { admin } = useAuth();
  return (
    <>
      <PageHeader
        title="Ask Lume"
        meta={
          canChat ? (
            <span>The in-app chat, answered by Claude in Lume's voice: settings, usage and cost, and the latest turns for quality review.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Usage and settings need the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      <div className={al.stack}>
        <PrivacyNote />
        {canChat ? <AdminView /> : null}
      </div>
    </>
  );
}

function PrivacyNote() {
  return (
    <div className={m.notice} data-tone="info" data-testid="privacy-note">
      <strong>What goes to Anthropic, and what we keep</strong>
      <span>
        Each question goes to Anthropic's Claude (through the puzzle engine, with the same key as translation) together with the recent chat turns (up to 12) and a short
        progress summary without the player's handle. Players agree to this in the app before their first question. Turns (question, answer, model, tokens, cost) are kept
        here for 30 days for quality review and the daily limits, then deleted.
      </span>
    </div>
  );
}

function AdminView() {
  const today = todayUtc();
  const last30 = addDays(today, -29);
  const monthStart = `${today.slice(0, 8)}01`;
  // Fetch the last 30 days and the whole month, whichever starts earlier (month-to-date against the budget).
  const range = { from: monthStart < last30 ? monthStart : last30, to: today };
  const stats = useChatStats(range, true);
  const config = useConfig();
  const chat = readLumeConfig(config.data?.values).chat;

  if (stats.isPending || config.isPending) {
    return (
      <Card>
        <Loading rows={8} />
      </Card>
    );
  }
  if (stats.isError) return <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />;

  const byDate = new Map(stats.data.days.map((d) => [d.date, d]));
  const days = Array.from({ length: 30 }, (_, i) => {
    const date = addDays(last30, i);
    return byDate.get(date) ?? { date, questions: 0, players: 0, costUsd: 0, refused: 0, failed: 0 };
  });
  const sum = (k: "questions" | "costUsd" | "refused" | "failed") => days.reduce((s, d) => s + d[k], 0);
  const mtd = stats.data.days.filter((d) => d.date >= monthStart).reduce((s, d) => s + d.costUsd, 0);
  const budgetShare = chat.monthlyBudgetUsd > 0 ? mtd / chat.monthlyBudgetUsd : null;
  const peakPlayers = Math.max(0, ...days.map((d) => d.players));
  const questions = sum("questions");

  return (
    <>
      <div className={al.grid}>
        <SettingsCard chat={chat} prices={Object.keys((config.data?.values["studio.prices"] as Record<string, unknown> | undefined) ?? {})} />
        <Card title="Usage · last 30 days">
          <div className={styles.statStrip} data-testid="chat-usage">
            <Stat label="Questions" value={fmtInt(questions)} sub={`${fmtInt(Math.round(questions / 30))} a day`} />
            <Stat label="Players" value={fmtInt(peakPlayers)} sub="on the busiest day" />
            <Stat label="Cost" value={usd(sum("costUsd"))} sub={questions ? `${usd(sum("costUsd") / questions, 4)} a question` : "—"} />
            <Stat label="Refused" value={fmtInt(sum("refused"))} sub={questions ? `${fmtPct(sum("refused") / questions, 1)} of questions` : "off-topic, advice, answers"} />
            <Stat label="Failed" value={fmtInt(sum("failed"))} sub="engine or model errors" />
          </div>
          <div className={al.budget} data-testid="budget">
            <div className={al.budgetHead}>
              <span className="monoLabel">Month to date</span>
              <span>
                <strong className="mono">{usd(mtd)}</strong> of {usd(chat.monthlyBudgetUsd, 0)} budget
                {budgetShare !== null ? ` · ${fmtPct(budgetShare)}` : ""}
              </span>
            </div>
            <div className={styles.meterWide} data-tone={budgetShare !== null && budgetShare >= 0.8 ? "accent" : undefined}>
              <span style={{ width: `${Math.min(100, (budgetShare ?? 0) * 100)}%` }} />
            </div>
            {budgetShare !== null && budgetShare >= 1 ? (
              <p className={al.over}>Over the budget: Lume is resting until next month (players get 503 ai_unavailable).</p>
            ) : budgetShare !== null && budgetShare >= 0.8 ? (
              <p className={al.over}>Close to the budget: at 100% Lume rests until next month.</p>
            ) : null}
          </div>
          <div className={al.charts}>
            <div>
              <span className="monoLabel">Questions per day</span>
              <ColumnChart
                label="Ask Lume questions per day"
                valueName="questions"
                rows={days.map((d) => ({
                  key: d.date,
                  label: formatShortDate(d.date),
                  value: d.questions,
                  notes: [`${fmtInt(d.players)} players`, `${fmtInt(d.refused)} refused · ${fmtInt(d.failed)} failed`],
                }))}
              />
            </div>
            <div>
              <span className="monoLabel">Cost per day (USD)</span>
              <ColumnChart
                label="Ask Lume cost per day in USD"
                valueName=""
                format={(v) => usd(v, v < 1 ? 2 : 0)}
                integerTicks={false}
                rows={days.map((d) => ({ key: d.date, label: formatShortDate(d.date), value: d.costUsd, notes: [`${fmtInt(d.questions)} questions`] }))}
              />
            </div>
          </div>
        </Card>
      </div>
      <RecentTurns turns={stats.data.recent} />
    </>
  );
}

// ---------------------------------------------------------------- settings

const FIELD_LABEL: Record<keyof LumeChatConfig, string> = {
  enabled: "On",
  freeDaily: "Free questions / day",
  proDaily: "Pro questions / day",
  model: "Model",
  maxOutputTokens: "Max reply tokens",
  monthlyBudgetUsd: "Monthly budget (USD)",
};

function SettingsCard({ chat, prices }: { chat: LumeChatConfig; prices: string[] }) {
  const set = useSetConfig();
  const [draft, setDraft] = useState(() => ({ ...chat, freeDaily: String(chat.freeDaily), proDaily: String(chat.proDaily), maxOutputTokens: String(chat.maxOutputTokens), monthlyBudgetUsd: String(chat.monthlyBudgetUsd) }));
  const [confirming, setConfirming] = useState(false);
  const value: LumeChatConfig = {
    enabled: draft.enabled,
    freeDaily: Number(draft.freeDaily),
    proDaily: Number(draft.proDaily),
    model: draft.model,
    maxOutputTokens: Number(draft.maxOutputTokens),
    monthlyBudgetUsd: Number(draft.monthlyBudgetUsd),
  };
  const models = prices.length ? prices : [chat.model];
  const problem = chatProblem(value, models);
  const changes = (Object.keys(FIELD_LABEL) as (keyof LumeChatConfig)[]).filter((k) => value[k] !== chat[k]);
  const num = (k: "freeDaily" | "proDaily" | "maxOutputTokens" | "monthlyBudgetUsd", label: string, hint: string, step = 1) => (
    <Field label={label} hint={hint}>
      <TextInput type="number" min={0} step={step} value={draft[k]} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} data-testid={`chat-${k}`} />
    </Field>
  );
  return (
    <Card title="Settings" actions={<Chip tone={chat.enabled ? "success" : "muted"}>{chat.enabled ? "on" : "off"}</Chip>}>
      <div className={al.settings} data-testid="chat-settings">
        <SwitchRow
          title="Ask Lume is on"
          hint="Off: the chat answers 503 and Lume “is resting”. The app keeps its Talk to a person link."
          checked={draft.enabled}
          onChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
          label="Ask Lume is on"
          testId="chat-enabled"
        />
        <div className={al.twoUp}>
          {num("freeDaily", FIELD_LABEL.freeDaily, "Per local day. Over it: 429 and an upgrade prompt.")}
          {num("proDaily", FIELD_LABEL.proDaily, "Per local day.")}
        </div>
        <Field label="Model" hint={prices.includes(chat.model) ? "From studio.prices: the cost is priced with it." : `${chat.model} has no price in studio.prices: pick one that has.`}>
          <Select value={draft.model} onChange={(e) => setDraft((d) => ({ ...d, model: e.target.value }))} data-testid="chat-model">
            {[...new Set([...models, chat.model])].map((x) => (
              <option key={x} value={x}>
                {x}
                {prices.includes(x) ? "" : " (no price)"}
              </option>
            ))}
          </Select>
        </Field>
        <div className={al.twoUp}>
          {num("maxOutputTokens", FIELD_LABEL.maxOutputTokens, "50–4000. Replies are short by design.")}
          {num("monthlyBudgetUsd", FIELD_LABEL.monthlyBudgetUsd, "Over it, Lume rests until next month.", 0.01)}
        </div>
        <div className={al.saveRow}>
          {problem ? (
            <span className={m.required} data-testid="chat-problem">
              {problem}
            </span>
          ) : changes.length ? (
            <span className={styles.faint}>
              {changes.length} unsaved {changes.length === 1 ? "change" : "changes"}
            </span>
          ) : null}
          <Button variant="primary" disabled={problem !== null || changes.length === 0} onClick={() => setConfirming(true)} data-testid="chat-save">
            Save…
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        title="Save Ask Lume settings?"
        confirmLabel="Save"
        busy={set.isPending}
        error={set.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => set.mutate({ key: "lume.chat", value }, { onSuccess: () => setConfirming(false) })}
      >
        <p className={styles.prose}>
          Writes the config key <code>lume.chat</code>; the API reads it on the next question.
        </p>
        <ul className={al.changes} data-testid="chat-changes">
          {changes.map((k) => (
            <li key={k}>
              <span>{FIELD_LABEL[k]}</span>
              <span className="mono">
                {String(chat[k])} → {String(value[k])}
              </span>
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    </Card>
  );
}

// ---------------------------------------------------------------- recent turns

type Filter = "all" | ChatStatus;

function RecentTurns({ turns }: { turns: ChatTurn[] }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<Set<number>>(new Set());
  const shown = turns.map((t, i) => ({ t, i })).filter(({ t }) => filter === "all" || t.status === filter);
  const count = (f: Filter) => (f === "all" ? turns.length : turns.filter((t) => t.status === f).length);
  return (
    <Card
      title="Recent turns"
      padded={false}
      actions={
        <Segmented
          label="Status"
          value={filter}
          onChange={setFilter}
          options={(["all", ...CHAT_STATUSES] as const).map((f) => ({ value: f, label: `${f === "all" ? "All" : f[0]!.toUpperCase() + f.slice(1)} · ${count(f)}` }))}
        />
      }
    >
      {shown.length === 0 ? (
        <EmptyState title="No turns">{filter === "all" ? "Questions players ask show up here for 30 days." : `No ${filter} turns among the latest 100.`}</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>When</th>
                <th>Player</th>
                <th>Question</th>
                <th>Answer</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(({ t, i }) => {
                const expanded = open.has(i);
                const long = t.question.length > 90 || (t.answer?.length ?? 0) > 120;
                return (
                  <tr key={i} data-testid="turn" data-status={t.status}>
                    <td className={tableStyles.mono}>{formatDateTime(t.at)}</td>
                    <td>
                      <Link to={`/players/${t.userId}`} className={tableStyles.id}>
                        {t.handle ? `@${t.handle}` : shortId(t.userId)}
                      </Link>
                    </td>
                    <td>
                      <p className={al.text} data-expanded={expanded || undefined}>
                        {t.question}
                      </p>
                    </td>
                    <td>
                      <p className={al.text} data-expanded={expanded || undefined}>
                        {t.answer ?? <span className={styles.faint}>no answer</span>}
                      </p>
                      {long ? (
                        <button
                          type="button"
                          className={al.more}
                          onClick={() =>
                            setOpen((s) => {
                              const next = new Set(s);
                              if (next.has(i)) next.delete(i);
                              else next.add(i);
                              return next;
                            })
                          }
                          aria-expanded={expanded}
                        >
                          {expanded ? "Less" : "More"}
                        </button>
                      ) : null}
                    </td>
                    <td>
                      <Chip tone={STATUS_TONE[t.status]}>{t.status}</Chip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
